import React, { useEffect, useRef, useState } from "react";

function EnhancedPlayer({
  audioURL,
  Waiting,
  setDisabledButton,
  title,
  SubCategory,
  setIsAudioPlaying,
  audioPreloadStatus = "idle",
}) {
  const [timer, setTimer] = useState(Waiting);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [playbackState, setPlaybackState] = useState(audioURL ? "preparing" : "idle");
  const [recordingTime, setRecordingTime] = useState(0);
  
  const audioRef = useRef(null);

  // Start countdown and play only after the audio can actually play.
  useEffect(() => {
    if (!audioURL || !audioReady) return;

    let cancelled = false;
    const audio = audioRef.current;

    setTimer(Waiting);
    setPlaybackState("countdown");
    setIsAudioPlaying(true);
    setDisabledButton(true);

    const intervalId = setInterval(() => {
      setTimer(prev => {
        if (prev > 1) {
          return prev - 1;
        }
        // Reaching 0 now
        clearInterval(intervalId);
        if (!cancelled && audio) {
          const tryPlay = () => {
            if (audio.readyState >= 2) {
              audio.play().catch((error) => {
                console.error("Error playing audio:", error);
                setPlaybackState("error");
                setIsAudioPlaying(false);
                setDisabledButton(false);
              });
            } else {
              setTimeout(tryPlay, 100);
            }
          };
          tryPlay();
        }
        return 0;
      });
    }, 1000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [audioURL, audioReady, Waiting, setDisabledButton, setIsAudioPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onReady = () => {
      setAudioReady(true);
      setRecordingTime(audio.duration || 0);
      if (!hasStarted) {
        setPlaybackState("ready");
      }
    };
    const onLoadedMetadata = () => setRecordingTime(audio.duration || 0);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const onPlay = () => {
      setHasStarted(true);
      setPlaybackState("playing");
      setIsPlaying(true);
      setIsAudioPlaying(true);
      setDisabledButton(true);
    };
    const onPause = () => {
      setIsPlaying(false);
      if (!audio.ended) {
        setPlaybackState("paused");
      }
    };
    const onEnded = () => {
      setPlaybackState("ended");
      setIsPlaying(false);
      setIsAudioPlaying(false);
      setDisabledButton(false);
    };
    const onWaiting = () => {
      if (!audio.ended) {
        setPlaybackState("buffering");
        setIsAudioPlaying(true);
        setDisabledButton(true);
      }
    };
    const onError = (e) => {
      console.error("Audio error:", e);
      setPlaybackState("error");
      setIsPlaying(false);
      setIsAudioPlaying(false);
      setDisabledButton(false);
    };

    audio.addEventListener("canplay", onReady);
    audio.addEventListener("canplaythrough", onReady);
    audio.addEventListener("loadeddata", onReady);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("stalled", onWaiting);
    audio.addEventListener("error", onError);

    return () => {
      audio.removeEventListener("canplay", onReady);
      audio.removeEventListener("canplaythrough", onReady);
      audio.removeEventListener("loadeddata", onReady);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("stalled", onWaiting);
      audio.removeEventListener("error", onError);
    };
  }, [audioURL, hasStarted, setDisabledButton, setIsAudioPlaying]);

  // Reset playback position when audioURL changes
  useEffect(() => {
    setTimer(Waiting);
    setCurrentTime(0);
    setIsPlaying(false);
    setAudioReady(false);
    setHasStarted(false);
    setRecordingTime(0);
    setPlaybackState(audioURL ? "preparing" : "idle");
    setIsAudioPlaying(!!audioURL);
    setDisabledButton(!!audioURL);

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      if (audioURL) {
        audio.load();
      }
    }
  }, [audioURL, Waiting, setDisabledButton, setIsAudioPlaying]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  const getStatusBadge = () => {
    if (!audioURL) {
      return { text: "No audio", color: "#ef4444" };
    } else if (playbackState === "error" || audioPreloadStatus === "error") {
      return { text: "Audio unavailable", color: "#ef4444" };
    } else if (!audioReady) {
      return { text: "Preparing audio...", color: "#6366f1" };
    } else if (playbackState === "buffering") {
      return { text: "Buffering audio...", color: "#6366f1" };
    } else if (timer > 0 && playbackState === "countdown") {
      return { text: `Starting in ${timer}s`, color: "#f59e0b" };
    } else if (isPlaying) {
      return { text: "Playing", color: "#10b981" };
    } else if (playbackState === "ended") {
      return { text: "Playback Finished", color: "#6b7280" };
    } else {
      return { text: "Ready", color: "#6b7280" };
    }
  };

  const totalDuration = recordingTime || 40;
  const progressPercent = totalDuration > 0 ? Math.min(100, Math.max(0, (currentTime / totalDuration) * 100)) : 0;

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          backgroundColor: "rgba(153, 108, 254, 0.10)",
          maxWidth: "350px",
          borderRadius: "8px",
          padding: "15px",
          margin: "30px auto",
        }}
      >
        <StatusRow>
          <StatusBadge color={getStatusBadge().color}>
            {getStatusBadge().text}
          </StatusBadge>
        </StatusRow>

        <div>
          <audio 
            ref={audioRef} 
            src={audioURL} 
            preload="auto"
          >
            Your browser does not support the audio element.
          </audio>
          <ProgressWrapper>
            <ProgressTrack />
            <ProgressFill percent={progressPercent} />
            <ProgressThumb percent={progressPercent} />
            <input
              type="range"
              value={currentTime}
              min="0"
              max={totalDuration}
              step="0.1"
              className="slider"
              onChange={() => {}}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                width: '100%',
                height: 18,
                opacity: 0,
                cursor: 'default',
              }}
            />
          </ProgressWrapper>
        </div>
      </div>
    </div>
  );
}

export default React.memo(EnhancedPlayer);

// Styled Components
function StatusRow({ children }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
      {children}
    </div>
  );
}

function StatusBadge({ color, children }) {
  return (
    <span
      style={{
        fontSize: "12px",
        padding: "3px 8px",
        borderRadius: "999px",
        color: "#fff",
        backgroundColor: color,
      }}
    >
      {children}
    </span>
  );
}

function ProgressWrapper({ children }) {
  return (
    <div style={{ 
      position: 'relative', 
      width: '100%', 
      height: 18, 
      display: 'flex', 
      alignItems: 'center', 
      marginTop: 10 
    }}>
      {children}
    </div>
  );
}

function ProgressTrack() {
  return (
    <div style={{ 
      position: 'absolute', 
      left: 0, 
      right: 0, 
      height: 6, 
      background: '#e2e2ea', 
      borderRadius: 4 
    }} />
  );
}

function ProgressFill({ percent }) {
  return (
    <div style={{ 
      position: 'absolute', 
      left: 0, 
      width: `${percent}%`, 
      height: 6, 
      background: '#996cfe', 
      borderRadius: 4 
    }} />
  );
}

function ProgressThumb({ percent }) {
  return (
    <div style={{ 
      position: 'absolute', 
      left: `calc(${percent}% - 6px)`, 
      width: 12, 
      height: 12, 
      borderRadius: 999, 
      background: '#996cfe' 
    }} />
  );
}
